import React from 'react';
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { Fade } from 'react-reveal';
import styled from 'styled-components';
import { Button } from '@makeship/core';
import Head from '../components/Head';
import { P1, H1, H1Styles, H2, H4Styles, H5, P2Styles, S1Styles, H8 } from '../components/Typography';

import config from '../../config.json';
import Container from '../components/Container';
import { getPropsForPageLoad } from '../utils/pageLoad';

const { routes } = config;

const AboutUsContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.oceanBlue};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0;
  margin-bottom: -48px;
`;

const AboutUsMastheadBackground = styled.div`
  background: linear-gradient(180deg, #5349ed 48.96%, #7f8af6 100%);
  width: 100%;
`;

const Header = styled(Container)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 72px 80px 100px 72px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
    padding: 106px 16px 44px 16px;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Heading = styled(H1)`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutral1};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${H4Styles}
    color: ${({ theme }) => theme.colors.neutral1};
    text-align: center;
  }
`;

const GreenText = styled.span`
  ${H1Styles}
  color: ${({ theme }) => theme.colors.greenNeutral};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${H4Styles}
    color: ${({ theme }) => theme.colors.greenNeutral};
  }
`;

const HeadingSub = styled(H8)`
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.neutral1};
  max-width: 465px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    text-align: center;
    margin-bottom: 44px;
    max-width: 100vw;
  }
`;

const HeaderImages = styled.div`
  display: flex;
  flex-direction: row;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
  }
`;

const HeaderImage = styled.img`
  max-height: 550px;
  width: auto;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    max-height: 450px;
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ValueContainerBackground = styled.div`
  background: linear-gradient(180deg, #7f8af6 0%, #90a3f9 11.46%, #9eb7fc 27.6%, #abcbff 100%);
  width: 100%;
`;

const ValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 64px 72px 64px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    padding: 80px 16px 0px 16px;
  }
`;

const ValueTitle = styled(H2)`
  text-transform: uppercase;
  margin-bottom: 96px;
  color: ${({ theme }) => theme.colors.primaryDark};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${H4Styles}
    color: ${({ theme }) => theme.colors.primaryDark};
    margin-bottom: 32px;
  }
`;

const ValueBadgesRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 64px;
  margin-bottom: 48px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
    margin-bottom: 48px;
  }
`;

const ValueBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BadgeImage = styled.img`
  height: 240px;
  object-fit: contain;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
  }
`;

const BadgeTitle = styled(H5)`
  font-weight: bold;
  margin: 16px 0;
  color: ${({ theme }) => theme.colors.black};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${S1Styles}
    color: ${({ theme }) => theme.colors.black};
  }
`;

const BadgeContent = styled(P1)`
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
  max-width: 544px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    ${P2Styles}
    color: ${({ theme }) => theme.colors.black};
    max-width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: 72px;
  gap: 40px;
  margin-top: 48px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: column;
    margin-bottom: 64px;
    margin-top: 32px;
  }
`;

const AboutUsPage: NextPage = () => (
  <>
    <Head
      title="About Us"
      url="/about-us"
      description="Makeship is a Canadian crowdfunding platform for content creators. Through collaborations with creators, we enable the design and creation of high-quality, limited edition custom products and experiences. We handle design, manufacturing, fulfilment & customer service."
    />
    <AboutUsContainer>
      <AboutUsMastheadBackground>
        <Header>
          <HeaderText>
            <Heading>
              A product platform for <GreenText>content creators</GreenText>
            </Heading>
            <HeadingSub>
              Makeship is a crowdfunding platform. Through collaborations with creators, we enable the design and
              creation of high-quality, limited edition custom products and experiences. We handle design,
              manufacturing, fulfilment &amp; customer service.
            </HeadingSub>
          </HeaderText>
          <HeaderImages>
            <Fade bottom fraction={0.6} distance="150%">
              <HeaderImage src="assets/about-us/about-us-collage.png" />
            </Fade>
          </HeaderImages>
        </Header>
      </AboutUsMastheadBackground>
      <ValueContainerBackground>
        <ValueContainer>
          <ValueTitle>Our Values</ValueTitle>
          <ValueBadgesRow>
            <ValueBadge>
              <BadgeImage src="/assets/about-us/make-ship-happen.png" />
              <BadgeTitle>Make Ship Happen</BadgeTitle>
              <BadgeContent>
                The core of what we do is support creators in doing what they love. When they run campaigns with us,
                they are able to monetize their creativity to support themselves and their families, and connect with
                their fanbase in a meaningful way.
              </BadgeContent>
            </ValueBadge>
            <ValueBadge>
              <BadgeImage src="/assets/about-us/make-good.png" />
              <BadgeTitle>Make Good</BadgeTitle>
              <BadgeContent>
                From the very beginning, limiting our impact on the environment has been integral to Makeship’s
                development and will always influence our daily decisions. That is why we have initiatives like carbon
                offsetting, reusable packaging and recyclable mailer bags.
              </BadgeContent>
            </ValueBadge>
          </ValueBadgesRow>
          <ValueBadgesRow>
            <ValueBadge>
              <ValueBadge>
                <BadgeImage src="/assets/about-us/make-communities.png" />
                <BadgeTitle>Make Communities</BadgeTitle>
                <BadgeContent>
                  We have the unique ability to combine multiple groups of creative people together within our
                  community, including creators, brands, fans, and the Makeship team. We want to cultivate this group
                  into a super community that can advance the creator economy movement.
                </BadgeContent>
              </ValueBadge>
            </ValueBadge>
          </ValueBadgesRow>
          <ButtonContainer>
            <Link href={routes.shopLive} passHref>
              <Button.Primary>Explore Campaigns</Button.Primary>
            </Link>
            <Link href={routes.careers} passHref>
              <Button.Primary>Join Our Team</Button.Primary>
            </Link>
          </ButtonContainer>
        </ValueContainer>
      </ValueContainerBackground>
    </AboutUsContainer>
  </>
);

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const propsForPageLoad = await getPropsForPageLoad(ctx);

  return {
    props: {
      ...propsForPageLoad,
    },
  };
};

export default AboutUsPage;
